import { AuthTracking } from "@/lib/tracking/auth";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  InputPassword,
  Label,
  Typography,
  Stack,
  LoadingButton,
} from "@altimateai/lego";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { BaseContainer } from "./components/base";
import { useMutation } from "@tanstack/react-query";
import { postLogin, verifyFreeUser, postUserOnboard } from "@/helpers/apis";
import { loginUser, setRememberMe } from "@/services/auth";
import { getDefaultRoute, getTenant, isFreeStack } from "@/helpers/tenant";
import { decodeToken } from "react-jwt";
import { isMobile } from "@/helpers/utils";
import { useAppDispatch } from "@/modules/app/useAppContext";
import { setLoggedinUser } from "@/modules/app/appSlice";
import { IconCircleCheckFilled } from "@tabler/icons-react";
import { useReoTracking } from "@/hooks/useReoTracking";

const loginFormSchema = z.object({
  email: z.string().min(1, "Please Enter Your Email").email("Please enter a valid email address"),
  password: z.string().min(1, "Please Enter Your Password"),
});

type LoginFormValues = z.infer<typeof loginFormSchema>;

export const Login = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [expired] = useState(state?.expired);
  const { token } = useParams();
  const [remember, setRemember] = useState(false);
  const [message, setMessage] = useState("");
  const isFreeUser = isFreeStack();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const user = queryParams.get("user");
  const dispatch = useAppDispatch();
  const { identifyUser } = useReoTracking(isFreeUser);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    mode: "onTouched",
    defaultValues: {
      email: user ?? "",
      password: "",
    },
  });

  const { setValue } = form;

  const { mutate: onboardUser } = useMutation({
    mutationFn: postUserOnboard,
  });

  const {
    mutate: login,
    error,
    isPending,
  } = useMutation({
    mutationFn: postLogin,
    onSuccess: response => {
      if (response?.auth_token) {
        const loginType = isFreeUser ? "free" : "standard";
        AuthTracking.loginSucceeded(loginType, !!response?.is_onboarded);
        setRememberMe(remember);
        loginUser(response);
        dispatch(setLoggedinUser(response));

        // Identify user with Reo.dev for freemium tracking
        identifyUser(response);

        if (state?.from) navigate(state?.from);
        else if (isFreeUser) {
          if (!response?.is_onboarded) {
            onboardUser();
            navigate("/settings/api-key");
          } else {
            navigate("/quickstart");
          }
        } else navigate(getDefaultRoute(getTenant()));
      }
    },
    onError: (err) => {
      AuthTracking.loginFailed(
        isFreeUser ? "free" : "standard",
        err instanceof Error ? err.message : String(err)
      );
    },
  });

  useEffect(() => {
    if (!token) return;
    const decodedToken = decodeToken<{ email: string }>(token);
    if (!decodedToken) return navigate("/expired");
    setValue("email", decodedToken.email);
    (async () => {
      const data = await verifyFreeUser({ token });
      if (data?.ok) setMessage("We've verified your identity. Please sign in");
    })();
  }, [navigate, token, setValue]);

  useEffect(() => {
    if (!user) navigate(".", { replace: true, state: { ...state, expired: false } });
  }, []);

  const onSubmit = (values: LoginFormValues) => {
    AuthTracking.loginSubmitted(isFreeUser ? "free" : "standard");
    login(values);
  };

  const isMobileView = isMobile();

  return (
    <BaseContainer
      footer={
        isFreeStack() && (
          <div>
            <span className="al-text-muted-foreground al-font-regular">
              Don&#39;t have an account?{" "}
            </span>
            <Link to="https://app.myaltimate.com/register" className="text-primary">
              Register
            </Link>
          </div>
        )
      }
    >
      <div className={isMobileView ? "al-w-[90vw]" : ""}>
        {message && (
          <Alert variant="success" className="al-my-2">
            <IconCircleCheckFilled />
            <AlertTitle className={"al-text-base"}>Success</AlertTitle>
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}
        <Typography variant="page-heading">Sign In</Typography>
        {expired && (
          <Typography variant="body" size="sm" className="al-text-muted-foreground">
            Session expired. Please login again.
          </Typography>
        )}
        <Form {...form}>
          <form
            aria-label="Login form"
            onSubmit={form.handleSubmit(onSubmit)}
            className={`${isMobileView ? "al-w-full al-mt-3" : "al-mt-4"}`}
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="al-mb-3">
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="Enter email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="al-mb-3">
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <InputPassword placeholder="Enter password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Stack justify="between">
              <Stack align="center">
                <Checkbox
                  id="remember-check"
                  checked={remember}
                  onCheckedChange={(checked: boolean) => {
                    setRemember(checked);
                  }}
                />
                <Label className="al-text-muted-foreground al-font-normal" htmlFor="remember-check">
                  Remember me
                </Label>
              </Stack>
              <div className="al-float-right">
                <Link to="/forgot" className="al-text-primary">
                  Forgot password?
                </Link>
              </div>
            </Stack>

            {error && (
              <Alert variant="destructive" className="al-mt-3 al-mb-3">
                <AlertDescription>
                  {error instanceof Error ? error.message : String(error)}
                </AlertDescription>
              </Alert>
            )}

            <div className="al-mt-4">
              <LoadingButton
                className="al-w-full disabled:al-bg-action-gray disabled:al-border-action-gray"
                type="submit"
                loading={isPending}
              >
                Sign In
              </LoadingButton>
            </div>
          </form>
        </Form>
      </div>
    </BaseContainer>
  );
};
